

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { createPageUrl } from "@/utils";
import SEOTitle from "@/components/SEOTitle";
import {
  Phone,
  Menu,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const COMPANY_INFO = {
  name: "נובה קאי עובדים זרים בע״מ",
  phone: "050-6397179",
  email: "Office@novakai.co.il",
  address: "רחוב חמזה 9, אז\"ת כפר קאסם",
  hours: "ראשון-חמישי: 8:00-18:00",
  logoUrl: "/logo.webp",
  instagram: "https://www.instagram.com/novakai2024/",
  facebook: "https://www.facebook.com/profile.php?id=61585795867476",
  tiktok: "https://www.tiktok.com/@novakai2023"
};

const navigationItems = [
  { title: "דף הבית", path: "" },
  { title: "שירותים", path: "Services" },
  { title: "מדינות מקור", path: "Countries" },
  { title: "גלריה", path: "Gallery" },
  { title: "אודות", path: "About" },
  { title: "לקוחות", path: "Clients" },
  { title: "מאמרים", path: "Blog" },
  { title: "שאלות נפוצות", path: "FAQ" },
  { title: "צור קשר", path: "Contact" }
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add Social Media Meta Tags and Canonical Link
  useEffect(() => {
    // Open Graph Meta Tags
    const ogMetaTags = [
      { property: "og:type", content: "website" },
      { property: "og:title", content: "נובה קאי | חברת כוח אדם לעובדים זרים בענף הבנייה" },
      { property: "og:description", content: "חברת נובה קאי מתמחה באספקת עובדים זרים מקצועיים לכל תחומי הבנייה בישראל." },
      { property: "og:image", content: "https://www.novakai.co.il/og-image.webp" },
      { property: "og:url", content: "https://www.novakai.co.il" }
    ];

    // Add or update Open Graph meta tags
    ogMetaTags.forEach(({ property, content }) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("property", property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute("content", content);
    });

    // Add or update Twitter meta tag
    let twitterMeta = document.querySelector('meta[name="twitter:card"]');
    if (!twitterMeta) {
      twitterMeta = document.createElement("meta");
      twitterMeta.setAttribute("name", "twitter:card");
      document.head.appendChild(twitterMeta);
    }
    twitterMeta.setAttribute("content", "summary_large_image");

    // Add or update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", `https://www.novakai.co.il${location.pathname}`);
  }, [location.pathname]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.search]);

  return (
    <>
      <SEOTitle />
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800&family=Rubik:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div dir="rtl" className="min-h-screen bg-white overflow-x-hidden">
        <style>{`
          :root {
            --primary: #0F172A;
            --secondary: #1F2937;
            --accent: #E6B800;
            --success: #16A34A;
            --error: #DC2626;
            --border: #E5E7EB;
            --background: #FFFFFF;
          }
          
          * {
            direction: rtl;
          }
          
          body {
            font-family: 'Heebo', -apple-system, BlinkMacSystemFont, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            overflow-x: hidden;
          }
          
          html {
            overflow-x: hidden;
          }
          
          h1, h2, h3, h4, h5, h6 {
            font-family: 'Rubik', 'Heebo', sans-serif;
            font-weight: 700;
          }
          
          .text-primary { color: var(--primary); }
          .bg-primary { background-color: var(--primary); }
          .text-accent { color: var(--accent); }
          .bg-accent { background-color: var(--accent); }
          .border-accent { border-color: var(--accent); }
          
          a, button { transition: all 0.2s ease; }
          
          ::-webkit-scrollbar { width: 10px; }
          ::-webkit-scrollbar-track { background: #f1f1f1; }
          ::-webkit-scrollbar-thumb { 
            background: var(--primary); 
            border-radius: 5px;
          }
          ::-webkit-scrollbar-thumb:hover { background: var(--secondary); }

          /* Keyboard Navigation */
          *:focus-visible {
            outline: 3px solid #E6B800 !important;
            outline-offset: 2px !important;
          }
        `}</style>

        {/* Header */}
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link to={'/'} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img
                  src={COMPANY_INFO.logoUrl}
                  alt="נובה קאי"
                  className="h-16 w-auto"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden xl:flex items-center gap-1">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={createPageUrl(item.path)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${location.pathname === createPageUrl(item.path)
                      ? "bg-[#0F172A] text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    {item.title}
                  </Link>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="flex items-center gap-2">
                <a
                  href={`https://wa.me/972506397179`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#0F172A] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="hidden xl:inline">{COMPANY_INFO.phone}</span>
                </a>

                <Link to={createPageUrl("Contact")} className="hidden md:block">
                  <Button className="bg-[#E6B800] hover:bg-[#d4a800] text-[#0F172A] font-bold shadow-md hover:shadow-lg">
                    דברו איתי עכשיו
                  </Button>
                </Link>

                {/* Mobile Menu */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="xl:hidden hover:bg-gray-100">
                      <Menu className="w-6 h-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 bg-white">
                    <SheetHeader className="border-b border-gray-200 pb-4">
                      <SheetTitle className="text-right text-xl font-bold text-[#0F172A]">תפריט ראשי</SheetTitle>
                      <SheetDescription className="text-right text-sm text-gray-600">ניווט באתר</SheetDescription>
                    </SheetHeader>
                    <nav className="flex flex-col gap-2 mt-6">
                      {navigationItems.map((item) => (
                        <Link
                          key={item.path}
                          to={createPageUrl(item.path)}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`px-4 py-3 rounded-lg text-right font-medium transition-all ${location.pathname === createPageUrl(item.path)
                            ? "bg-[#0F172A] text-white shadow-md"
                            : "text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                          {item.title}
                        </Link>
                      ))}
                      <div className="border-t border-gray-200 my-4"></div>
                      <a
                        href={`https://wa.me/972506397179`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Phone className="w-5 h-5 text-[#0F172A]" />
                        <span className="font-medium">{COMPANY_INFO.phone}</span>
                      </a>
                    </nav>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="overflow-x-hidden">
          {children}
        </main>

        {/* Floating WhatsApp Button */}
        <a
          href={`https://wa.me/972506397179?text=${encodeURIComponent('שלום,\n\nאשמח לקבל הצעת מחיר לעובדים לפרוייקט שלנו.\n\nפרטי התקשרות:\nשם החברה:\nסיווג קבלן:\nאיש קשר:\nטלפון נייד:\nמיקום האתר:\n\nבברכה,')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300"
          aria-label="שלח הודעה בוואטסאפ"
        >
          <svg className="w-8 h-8 pl-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
        </a>

        {/* Footer */}
        <footer className="bg-linear-to-b from-[#0F172A] to-[#1a2332] text-white overflow-x-hidden">
          <div className="container mx-auto px-4 py-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
              {/* Company Info */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Link to="/" className="inline-block transition-transform hover:scale-105 duration-300">
                    <img
                      src={COMPANY_INFO.logoUrl}
                      alt="נובה קאי"
                      className="h-12 w-auto brightness-0 invert"
                    />
                  </Link>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  תאגיד מוסמך לאספקת כוח אדם זר לענף הבנייה. מעל 19 שנות ניסיון, אמינות ומקצועיות.
                </p>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#E6B800]">יצירת קשר</h3>
                <div className="space-y-3">
                  <a href={`https://wa.me/972506397179`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-300 hover:text-[#E6B800] transition-colors group">
                    <Phone className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">{COMPANY_INFO.phone}</span>
                  </a>
                  <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-3 text-gray-300 hover:text-[#E6B800] transition-colors group">
                    <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    <span className="text-sm">{COMPANY_INFO.email}</span>
                  </a>
                  <div className="flex items-start gap-3 text-gray-300">
                    <MapPin className="w-4 h-4 mt-1 shrink-0" />
                    <span className="text-sm">{COMPANY_INFO.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{COMPANY_INFO.hours}</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#E6B800]">קישורים מהירים</h3>
                <div className="space-y-2">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={createPageUrl(item.path)}
                      className="block text-sm text-gray-300 hover:text-[#E6B800] transition-colors hover:translate-x-1 transform"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Legal Information */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#E6B800]">הצהרות ותקנונים</h3>
                <div className="space-y-2">
                  <Link to={createPageUrl("accessibility-statement")} className="block text-sm text-gray-300 hover:text-[#E6B800] transition-colors hover:translate-x-1 transform">
                    הצהרת נגישות
                  </Link>
                  <Link to={createPageUrl("terms-of-use")} className="block text-sm text-gray-300 hover:text-[#E6B800] transition-colors hover:translate-x-1 transform">
                    תקנון ותנאי שימוש
                  </Link>
                  <Link to={createPageUrl("privacy-policy")} className="block text-sm text-gray-300 hover:text-[#E6B800] transition-colors hover:translate-x-1 transform">
                    מדיניות פרטיות
                  </Link>
                </div>
              </div>

              {/* Social & CTA */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#E6B800]">עקבו אחרינו</h3>
                <div className="flex gap-3 mb-6">
                  <a href={COMPANY_INFO.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#E6B800] rounded-lg flex items-center justify-center transition-all hover:scale-110" aria-label="פייסבוק">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  </a>
                  <a href={COMPANY_INFO.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#E6B800] rounded-lg flex items-center justify-center transition-all hover:scale-110" aria-label="טיקטוק">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 448 512"><path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" /></svg>
                  </a>
                  <a href="https://www.linkedin.com/company/novakai" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#E6B800] rounded-lg flex items-center justify-center transition-all hover:scale-110" aria-label="לינקדאין">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                  </a>
                  <a href={COMPANY_INFO.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#E6B800] rounded-lg flex items-center justify-center transition-all hover:scale-110" aria-label="אינסטגרם">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                  </a>
                </div>
                <Link to={createPageUrl("Contact")}>
                  <Button className="w-full bg-[#E6B800] hover:bg-[#d4a800] text-[#0F172A] font-bold shadow-lg hover:shadow-xl transition-all">
                    קבלו הצעת מחיר
                  </Button>
                </Link>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8 text-center">
              <p className="text-sm text-gray-400">
                © {new Date().getFullYear()} נובה קאי עובדים זרים בע״מ. כל הזכויות שמורות.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
